import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Form, Spinner } from 'react-bootstrap';
import useRentalApi from '../../hooks/useRentalApi';
import './RentalHeatmapWidget.css';

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function formatDateLabel(value) {
  const date = new Date(`${value}T00:00:00`);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatRange(startDate, endDate) {
  if (!startDate || !endDate) return 'Last 30 days';
  return `${formatDateLabel(startDate)} - ${formatDateLabel(endDate)}`;
}

function getIntensity(count, maxCount) {
  if (!count || !maxCount) return 0;
  const ratio = count / maxCount;
  if (ratio >= 0.8) return 4;
  if (ratio >= 0.55) return 3;
  if (ratio >= 0.3) return 2;
  return 1;
}

function getTooltip(day) {
  const stores = Array.isArray(day.stores) ? day.stores : [];
  const storeSummary = stores
    .filter(store => store.rental_count > 0)
    .map(store => `${store.store_name}: ${store.rental_count}`)
    .join('\n');

  return [
    `${formatDateLabel(day.date)}: ${day.rental_count} active rentals`,
    storeSummary,
  ].filter(Boolean).join('\n');
}

export default function RentalHeatmapWidget({ days = 30, showStoreFilter = false, stores = [] }) {
  const { fetchRentalHeatmap } = useRentalApi();
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStoreId, setSelectedStoreId] = useState('');

  const loadHeatmap = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = selectedStoreId ? { days, store_ids: selectedStoreId } : { days };
      const data = await fetchRentalHeatmap(params);
      setHeatmapData(data);
    } catch (err) {
      console.error('Failed to load rental heatmap:', err);
      setError('Failed to load rental heatmap');
    } finally {
      setLoading(false);
    }
  }, [days, fetchRentalHeatmap, selectedStoreId]);

  useEffect(() => {
    loadHeatmap();
  }, [loadHeatmap]);

  const cells = useMemo(() => {
    const heatmap = heatmapData?.heatmap || [];
    if (heatmap.length === 0) return [];

    const firstDate = new Date(`${heatmap[0].date}T00:00:00`);
    const leadingBlanks = firstDate.getDay();
    return [
      ...Array.from({ length: leadingBlanks }, (_, index) => ({ key: `blank-${index}`, blank: true })),
      ...heatmap.map(day => ({ ...day, key: day.date })),
    ];
  }, [heatmapData]);

  const maxCount = heatmapData?.max_count || 0;
  const totalRentals = heatmapData?.total_rentals || 0;

  return (
    <Card className="shadow-sm rental-heatmap-widget">
      <Card.Header className="text-white d-flex justify-content-between align-items-center rental-heatmap-header">
        <div className="rental-heatmap-title">
          <i className="fas fa-th me-2"></i>
          Rental Heatmap
        </div>
        <div className="d-flex align-items-center gap-2">
          <Badge bg="light" text="dark">{totalRentals} rental days</Badge>
          <Button variant="light" size="sm" onClick={loadHeatmap} disabled={loading}>
            <i className="fas fa-sync-alt"></i>
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="rental-heatmap-toolbar">
          <div className="rental-heatmap-meta">
            <span>{formatRange(heatmapData?.start_date, heatmapData?.end_date)}</span>
            <span>Peak {maxCount}</span>
          </div>
          {showStoreFilter && stores.length > 0 && (
            <Form.Select
              size="sm"
              value={selectedStoreId}
              onChange={(event) => setSelectedStoreId(event.target.value)}
              aria-label="Filter rental heatmap by store"
              className="rental-heatmap-store-filter"
            >
              <option value="">All Stores</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>
                  {store.name || `Store ${store.id}`}
                </option>
              ))}
            </Form.Select>
          )}
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : error ? (
          <div className="alert alert-warning mb-0">{error}</div>
        ) : (
          <>
            <div className="rental-heatmap-weekdays" aria-hidden="true">
              {DAY_LABELS.map((label, index) => (
                <span key={`${label}-${index}`}>{label}</span>
              ))}
            </div>
            <div className="rental-heatmap-grid" aria-label="Rental activity by day">
              {cells.map(cell => cell.blank ? (
                <span key={cell.key} className="rental-heatmap-cell rental-heatmap-cell-blank" />
              ) : (
                <span
                  key={cell.key}
                  className={`rental-heatmap-cell rental-heatmap-level-${getIntensity(cell.rental_count, maxCount)}`}
                  title={getTooltip(cell)}
                  aria-label={getTooltip(cell)}
                >
                  <span>{new Date(`${cell.date}T00:00:00`).getDate()}</span>
                  <strong>{cell.rental_count}</strong>
                </span>
              ))}
            </div>
            <div className="rental-heatmap-legend" aria-hidden="true">
              <span>Low</span>
              {[0, 1, 2, 3, 4].map(level => (
                <span key={level} className={`rental-heatmap-swatch rental-heatmap-level-${level}`} />
              ))}
              <span>High</span>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
